import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Bed, Bath, Maximize, X, MapPin } from 'lucide-react';
import SmartImage from '../components/SmartImage';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';
import { useFavorites } from '../context/FavoritesContext';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtPrice = (p) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(p || 0);

const Compare = () => {
  const { compare, toggleCompare, clearCompare, compareMax } = useFavorites();
  const { language } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (compare.length === 0) { setProperties([]); setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/api/xml/properties?limit=500&lang=${language}`)
      .then(r => r.json())
      .then(all => {
        const map = new Map(all.map(p => [p.id, p]));
        setProperties(compare.map(id => map.get(id)).filter(Boolean));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [compare, language]);

  // Highlight the lowest price + largest area + most bedrooms
  const minPrice = Math.min(...properties.map(p => p.price || Infinity));
  const maxBeds = Math.max(...properties.map(p => p.bedrooms || 0));
  const maxArea = Math.max(...properties.map(p => p.surface || 0));

  const HighlightCell = ({ value, isBest, suffix = '', children }) => (
    <td style={{ ...styles.cell, ...(isBest ? styles.cellBest : {}) }}>
      {children || (
        <span>
          {value}{suffix}
          {isBest && <span style={styles.bestBadge}>Najlepsza</span>}
        </span>
      )}
    </td>
  );

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '4rem' }}>
      <SEO titleKey="seo.propertiesTitle" descriptionKey="seo.propertiesDescription" noindex />
      <PageHero
        image="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80"
        title="Porównaj"
        subtitle={`Do ${compareMax} nieruchomości obok siebie`}
        testIdTitle="compare-title"
      />
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <Link to="/woningen" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#C5A059', textDecoration: 'none', fontSize: '0.9rem' }}>
            <ArrowLeft size={16} /> Powrót do nieruchomości
          </Link>
          {properties.length > 0 && (
            <button onClick={clearCompare} style={styles.clearBtn} data-testid="compare-clear-all">
              Wyczyść porównanie
            </button>
          )}
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#8C8B88' }}>Ładowanie...</p>
        ) : properties.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 1.5rem' }}>
            <BarChart3 size={56} color="#C5A059" style={{ marginBottom: '1rem' }} />
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: '#1A1918' }}>
              Brak nieruchomości do porównania
            </h2>
            <p style={{ color: '#585755', marginBottom: '1.5rem' }}>
              {`Kliknij ikonę porównania (📊) przy nieruchomości. Do ${compareMax} obok siebie.`}
            </p>
            <Link to="/woningen" style={{ background: '#C5A059', color: 'white', padding: '0.85rem 1.75rem', textDecoration: 'none', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.82rem' }}>
              Zobacz nieruchomości
            </Link>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }} data-testid="compare-table-wrapper">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.cell, ...styles.firstCol }}></th>
                  {properties.map(p => (
                    <th key={p.id} style={styles.cellHead}>
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          onClick={() => toggleCompare(p.id)}
                          style={styles.removeBtn}
                          aria-label="Verwijder"
                          data-testid={`compare-remove-${p.id}`}
                        ><X size={14} /></button>
                        <Link to={`/woning/${p.id}`}>
                          <SmartImage
                            src={p.images[0]}
                            alt={p.title}
                            width={400}
                            style={{ width: '100%', height: '180px', marginBottom: '0.75rem' }}
                          />
                        </Link>
                        <Link to={`/woning/${p.id}`} style={styles.propLink}>
                          <strong>{p.title}</strong>
                        </Link>
                        <div style={styles.subLine}><MapPin size={12} /> {p.location}</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr><td style={styles.label}>Cena</td>
                  {properties.map(p => (
                    <HighlightCell key={p.id} isBest={p.price === minPrice && properties.length > 1} value={fmtPrice(p.price)} />
                  ))}
                </tr>
                <tr><td style={styles.label}><Bed size={14} /> Sypialnie</td>
                  {properties.map(p => (
                    <HighlightCell key={p.id} isBest={p.bedrooms === maxBeds && p.bedrooms > 0 && properties.length > 1} value={p.bedrooms || '—'} />
                  ))}
                </tr>
                <tr><td style={styles.label}><Bath size={14} /> Łazienki</td>
                  {properties.map(p => (
                    <td key={p.id} style={styles.cell}>{p.bathrooms || '—'}</td>
                  ))}
                </tr>
                <tr><td style={styles.label}><Maximize size={14} /> Powierzchnia</td>
                  {properties.map(p => (
                    <HighlightCell key={p.id} isBest={p.surface === maxArea && p.surface > 0 && properties.length > 1} value={p.surface ? `${p.surface} m²` : '—'} />
                  ))}
                </tr>
                <tr><td style={styles.label}>Typ</td>
                  {properties.map(p => (
                    <td key={p.id} style={styles.cell}>{p.property_type || '—'}</td>
                  ))}
                </tr>
                <tr><td style={styles.label}>Nowe budownictwo</td>
                  {properties.map(p => (
                    <td key={p.id} style={styles.cell}>{p.new_build ? '✓' : '—'}</td>
                  ))}
                </tr>
                <tr><td style={styles.label}>Basen</td>
                  {properties.map(p => (
                    <td key={p.id} style={styles.cell}>{p.pool ? '✓' : '—'}</td>
                  ))}
                </tr>
                <tr><td style={styles.label}>Ref.</td>
                  {properties.map(p => (
                    <td key={p.id} style={{ ...styles.cell, fontFamily: 'monospace', fontSize: '0.8rem', color: '#8C8B88' }}>{p.ref}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  table: { borderCollapse: 'collapse', width: '100%', backgroundColor: 'white' },
  cellHead: { padding: '1rem 1rem 0.75rem', verticalAlign: 'top', borderBottom: '1px solid #EAE5DD', minWidth: '220px' },
  cell: { padding: '0.85rem 1rem', borderBottom: '1px solid #F0EAE0', textAlign: 'center', color: '#1A1918' },
  cellBest: { backgroundColor: '#FFF8E7', fontWeight: 700, color: '#92400E' },
  bestBadge: { display: 'inline-block', marginLeft: '0.4rem', fontSize: '0.65rem', backgroundColor: '#C5A059', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '2px', fontWeight: 600, letterSpacing: '0.05em' },
  label: { padding: '0.85rem 1rem', borderBottom: '1px solid #F0EAE0', color: '#585755', fontSize: '0.82rem', textAlign: 'left', backgroundColor: '#FAF9F6', whiteSpace: 'nowrap' },
  firstCol: { width: '160px', backgroundColor: '#FAF9F6' },
  removeBtn: {
    position: 'absolute', top: 4, right: 4, zIndex: 1,
    width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'white', border: '1px solid #EAE5DD', cursor: 'pointer', color: '#1A1918',
  },
  propLink: { color: '#1A1918', textDecoration: 'none', display: 'block', marginBottom: '0.25rem', fontSize: '0.92rem' },
  subLine: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#8C8B88', fontSize: '0.78rem' },
  clearBtn: {
    background: 'transparent', borderWidth: '1px', borderStyle: 'solid', borderColor: '#EAE5DD',
    color: '#8C8B88', padding: '0.5rem 1rem', fontSize: '0.78rem', cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
  },
};

export default Compare;
